import { Component, Input, EventEmitter, Output, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ENTER } from '@angular/cdk/keycodes';
import { FilterName, UserItemEnum } from '../../../../store/models/filter.model';
import { parseChipsInputBulkDataEntry, removeInputElement } from '../../../../common/utils/helpers';
import { MatChipsFilterDialogComponent } from './mat-chips-filter-dialog/mat-chips-filter-dialog.component';
import { AutoUnsubscribe, Base, DIALOG_DEFAULT_CONFIG, DialogSize, IconNames } from '../../../../common';
import { FilterPopupChipsModel } from '../../../../store/state/monitoring/models/filter-popup-chips.model';
import {
  AllocationViewAvailableCompartment,
  AllocationViewAvailableCompartmentDock,
  AllocationViewAvailableDocksModel,
  AllocationViewFilterName,
  HubBrief,
  PartnerBrief,
  FilterOptionsFacade,
  TRANSLATED_FILTER_TITLES,
  UserListFilterName,
  UserRolesModel,
} from '../../../../store';
import { CompartmentDocksFilterSelectionDialogComponent } from './compartment-docks-filter-selection-dialog/compartment-docks-filter-selection-dialog.component';
import { DraggableFilterType } from '../../../../store/constants';
import { UserRolesFilterDialogComponent } from './user-roles-filters-dialog/user-roles-filter-dialog.component';
import { HubsFilterDialogComponent } from './hubs-filters-dialog/hubs-filter-dialog.component';
import { PartnersSelectionFilterDialogComponent } from './partners-filter-dialog/partners-selection-filter-dialog.component';
import { PartnersSelectionItem } from '../../../../container-components';

@Component({
  selector: 'ui-monitoring-filters-field',
  templateUrl: './monitoring-filters-field.component.html',
  styleUrls: ['./monitoring-filters-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// todo: split class to different specific fields components
@AutoUnsubscribe
export class MonitoringFiltersFieldComponent {
  @Input() filterTypeByName: Map<FilterName, DraggableFilterType>;
  @Input() filterSelectOptionsByName: Map<FilterName, string[] | Base[]>;
  @Input() filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup;
  @Input() isDraggedMode = false;
  @Input() isFieldEnabled = true;
  @Input() type: DraggableFilterType;
  @Input() name: FilterName;
  @Input() label: string;
  @Input() optionValueKeyForChipsInputSearchGeneric?: string[];
  @Input() optionNameKeyForChipsInputSearchGeneric?: string[];
  @Input() filterRadioOptionsByName?: Map<FilterName, string[] | UserRolesModel>;
  @Input() searchCheckboxAutocompleteWidth?: string | number;
  @Input() splitPattern?: RegExp;
  @Output() changeFieldState = new EventEmitter<FilterName>();
  selectedLocation: string;

  get selectedChipsInputSearchItems(): string {
    return this.filtersFormControlOrGroup.value;
  }

  allocationViewFilterName = AllocationViewFilterName;
  filterType = DraggableFilterType;
  chipsInputControl: UntypedFormControl = new UntypedFormControl('');
  separatorKeysCodes: number[] = [ENTER];
  removeFilter: (control: UntypedFormControl | UntypedFormGroup, element: string) => void = removeInputElement;
  translatedFilterTitles: Map<string, string> = TRANSLATED_FILTER_TITLES;
  userItemEnum = UserItemEnum;
  userListFilterName = UserListFilterName;
  iconNameList = IconNames;

  constructor(
    public cd: ChangeDetectorRef,
    public filterOptionsFacade: FilterOptionsFacade,
    private dialog: MatDialog,
  ) {}

  addElement(
    control: UntypedFormControl | AbstractControl,
    input: HTMLInputElement,
    inputControl: UntypedFormControl,
  ): void {
    parseChipsInputBulkDataEntry(control, input, inputControl, this.splitPattern);
  }

  openFilterDialog(
    filterName: string,
    label: string,
    filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup,
  ): void {
    const dialogRef = this.dialog.open(MatChipsFilterDialogComponent, {
      width: DialogSize.XL,
      data: {
        filterName,
        label,
        filtersFormControlOrGroup,
      },
    });

    dialogRef.afterClosed().subscribe((event: { selectedItems: FilterPopupChipsModel[] }) => {
      if (event?.selectedItems?.length) {
        this.filtersFormControlOrGroup.setValue(event?.selectedItems);
        this.filtersFormControlOrGroup.markAsDirty();
      }
    });
  }

  openFilterCheckboxDialog(label: string, filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup): void {
    if (this.name.match(this.userListFilterName.PartnerOwners)) {
      const dialogRef = this.dialog.open(PartnersSelectionFilterDialogComponent, {
        data: filtersFormControlOrGroup.value,
        ...DIALOG_DEFAULT_CONFIG,
        width: DialogSize.LG,
      });

      dialogRef.afterClosed().subscribe((selectedPartners: PartnersSelectionItem[]) => {
        if (selectedPartners) {
          this.filtersFormControlOrGroup.setValue(selectedPartners);
        }
        this.filtersFormControlOrGroup.markAsDirty();
      });
    } else {
      const dialogRef = this.dialog.open(CompartmentDocksFilterSelectionDialogComponent, {
        width: DialogSize.XL,
        data: {
          label,
          filtersFormControlOrGroup,
        },
      });

      dialogRef.afterClosed().subscribe((event: { selectedItems: AllocationViewAvailableDocksModel }) => {
        this.filtersFormControlOrGroup.setValue(event?.selectedItems);
        this.filtersFormControlOrGroup.markAsDirty();
      });
    }
  }

  removeCompartment(
    filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup,
    compartment: AllocationViewAvailableCompartment,
  ): void {
    const newCompartmentDockStructure = filtersFormControlOrGroup.value.compartments.reduce(
      (acc: AllocationViewAvailableDocksModel, compartmentItem: AllocationViewAvailableCompartment) => {
        if (compartment?.id !== compartmentItem?.id) {
          acc.compartments.push(compartmentItem);
        }
        return acc;
      },
      { compartments: [] },
    );

    filtersFormControlOrGroup.setValue(newCompartmentDockStructure);
    filtersFormControlOrGroup.markAsDirty();
  }

  removePartner(filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup, id: number): void {
    const partners: PartnerBrief[] = filtersFormControlOrGroup.value;
    filtersFormControlOrGroup.setValue(partners.filter((partner: PartnerBrief) => partner.id !== id));
    filtersFormControlOrGroup.markAsDirty();
  }

  removeDock(
    filtersFormControlOrGroup: UntypedFormControl | UntypedFormGroup,
    compartment: AllocationViewAvailableCompartment,
    dock: AllocationViewAvailableCompartmentDock,
  ): void {
    const newCompartmentDockStructure = { compartments: [] };

    filtersFormControlOrGroup.value.compartments.forEach((formCompartment: AllocationViewAvailableCompartment) => {
      if (compartment.id === formCompartment.id) {
        formCompartment.docks.forEach((dockItem: AllocationViewAvailableCompartmentDock) => {
          if (dockItem.id === dock.id) {
            const newDockList = formCompartment.docks.filter(
              (dockListItem: AllocationViewAvailableCompartmentDock) => dockListItem.id !== dock.id,
            );

            newCompartmentDockStructure.compartments.push({
              ...formCompartment,
              docks: newDockList,
            });
          }
        });
      } else {
        newCompartmentDockStructure.compartments.push({ ...formCompartment });
      }
    });

    filtersFormControlOrGroup.setValue(newCompartmentDockStructure);
    filtersFormControlOrGroup.markAsDirty();
  }

  openRadioDialog(): void {
    const data = this.filterRadioOptionsByName.get(this.name);
    if (this.name.match('assignedRole')) {
      const dialogRef = this.dialog.open(UserRolesFilterDialogComponent, {
        data: {
          roles: data,
          selectedRoles: this.filtersFormControlOrGroup.value,
        },
        ...DIALOG_DEFAULT_CONFIG,
      });

      dialogRef.afterClosed().subscribe((event: UserRolesModel) => {
        if (event) {
          this.filtersFormControlOrGroup.setValue(event);
          this.filtersFormControlOrGroup.markAsDirty();
        }
      });
    } else if (this.name.match('assignedLocation')) {
      const dialogRef = this.dialog.open(HubsFilterDialogComponent, {
        data,
        ...DIALOG_DEFAULT_CONFIG,
        width: DialogSize.LG,
      });

      dialogRef.afterClosed().subscribe((event: HubBrief) => {
        if (event) {
          this.filtersFormControlOrGroup.setValue(event);
          this.selectedLocation = event.name;
          this.filtersFormControlOrGroup.markAsDirty();
        }
      });
    }
  }

  remove(): void {
    this.filtersFormControlOrGroup.setValue(null);
    this.filtersFormControlOrGroup.markAsUntouched();
  }
}
